'use client';

import { useId } from 'react';
import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiBook2Line, RiCustomerService2Line, RiTerminalBoxLine } from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';

import { RiArrowRightUpLine } from '@/components/ui/rtl-icons';
import { Link } from '@/i18n/navigation';
import { apidocsHref } from '@/lib/apidocs-host';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * Kılavuzun bittiği yer — üç çıkış.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden bu bölüm var
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu sayfa "sistem nasıl kullanılıyor" sorusunu bir kere ve bütün olarak
 * cevaplıyor. Ama üç soru daha var ve üçünün de cevabı BAŞKA bir yerde:
 *
 *   • "Şu ekranda şu düğme ne yapıyor" → panel içindeki bilgi bankası.
 *     Ekran ekran yazılmış ve oturum arkasında; burada tekrar edilemez,
 *     çünkü okuyan kişinin kendi verisine bakıyor.
 *   • "Bunu koddan nasıl yaparım"       → apidocs.mentorivo.com.
 *   • "Benim durumum bunların hiçbiri"  → insan.
 *
 * Üçü de bir KAPI, bir içerik değil. Kart olmalarının sebebi bu: metnin
 * içine gömülmüş üç bağlantı, uzun bir sayfanın sonunda kaybolurdu.
 *
 * ⚠️ Dokümanın kartı ayrı alan adına gidiyor ve düz `<a>` ile basılıyor:
 * `@/i18n/navigation` içindeki `Link` mutlak adrese dil öneki eklemiyor,
 * doküman ise kendi dilini yalnızca yoldan okuyor. Öneksiz bırakılsaydı köke
 * düşen istek tarayıcının `Accept-Language`ına göre yönlenirdi — Türkçe
 * siteyi İngilizce tarayıcıda okuyan kişi İngilizce dokümana varırdı.
 * (Aynı ayrım `site-footer.tsx`te de var.)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Hareket ne söylüyor (DESIGN.md §4)
 * ═══════════════════════════════════════════════════════════════════════════
 * Kartlar sırayla oturuyor ve hover'da yalnızca ok kımıldıyor — kartın
 * kendisi değil. Bir kapı "buradan çıkılır" demeli, zıplamamalı; sayfanın
 * geri kalanı sakin ve kapanışın ondan daha yüksek sesli olması için bir
 * sebep yok (DESIGN.md §4: "sakinlik lükstür").
 *
 * `whileInView` + `once: true` — bölüm sayfanın en altında, açılışta ekranda
 * değil. Dokunmatikte `:hover` takılı kalmasın diye ok hareketi CSS'te
 * `@media (hover: hover)` içinde; buradaki varyantlar yalnız girişi taşıyor.
 */

type Route = {
  key: 'app' | 'api' | 'support';
  icon: ReactNode;
};

/**
 * Sıra okuma sırası: önce ürünün içi, sonra kod, sonra insan.
 *
 * Destek EN SONDA ve bu bilinçli — kullanıcıyı önce cevabın olabileceği iki
 * yere gönderiyoruz. Ters sıra, "önce sor, sonra ara" demek olurdu; panelin
 * sol menüsündeki Yardım grubu da aynı sırayı tutuyor (`lib/dash-nav.ts`).
 */
const ROUTES: readonly [Route, Route, Route] = [
  { key: 'app', icon: <RiBook2Line size={22} aria-hidden="true" /> },
  { key: 'api', icon: <RiTerminalBoxLine size={22} aria-hidden="true" /> },
  { key: 'support', icon: <RiCustomerService2Line size={22} aria-hidden="true" /> },
];

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.04 } },
};

const asideV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const listV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const cardV: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Kartları ayıran hat soldan sağa çiziliyor — listeyi açan jest. */
const ruleV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: { opacity: 1, scaleX: 1, transition: { duration: dur.slow, ease: ease.out } },
};

export function HelpRoutes() {
  const t = useTranslations('help.routes');
  const locale = useLocale();
  const uid = useId();

  return (
    <section className="hroute" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div
            className="hroute-inner"
            variants={sectionV}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.div className="hroute-aside" variants={asideV}>
              <p className="hroute-eyebrow">{t('eyebrow')}</p>
              <h2 className="hroute-title" id={`${uid}-title`}>
                {t.rich('title', { hl })}
              </h2>
              <p className="hroute-lead">{t('lead')}</p>
            </motion.div>

            <motion.ul className="hroute-list" variants={listV}>
              {ROUTES.map((route) => {
                const label = (
                  <>
                    <span className="hroute-icon" aria-hidden="true">
                      {route.icon}
                    </span>
                    <span className="hroute-copy">
                      <span className="hroute-name">{t(`items.${route.key}.title`)}</span>
                      <span className="hroute-desc">{t(`items.${route.key}.desc`)}</span>
                      <span className="hroute-cta">
                        {t(`items.${route.key}.cta`)}
                        <RiArrowRightUpLine className="hroute-arrow" size={16} aria-hidden="true" />
                      </span>
                    </span>
                  </>
                );

                return (
                  <motion.li className="hroute-item" key={route.key} variants={cardV}>
                    <motion.span className="hroute-rule" aria-hidden="true" variants={ruleV} />
                    {route.key === 'api' ? (
                      <a
                        className="hroute-card"
                        href={apidocsHref(locale)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {label}
                      </a>
                    ) : (
                      <Link
                        className="hroute-card"
                        href={route.key === 'app' ? '/dashboard/help' : '/contact'}
                      >
                        {label}
                      </Link>
                    )}
                  </motion.li>
                );
              })}
            </motion.ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
